import { z } from "zod/v3";

export const InterviewDifficultySchema = z.enum(["entry", "mid", "senior", "executive"]);
export const InterviewQuestionCategorySchema = z.enum([
  "behavioral",
  "technical",
  "situational",
  "role_specific",
]);
export const InterviewQuestionStageSchema = z.enum([
  "warmup",
  "core",
  "gap",
  "situational",
  "closing",
]);
export const InterviewRuntimePhaseSchema = z.enum([
  "ready",
  "opening",
  "awaiting_candidate_reply",
  "evaluating_answer",
  "asking_follow_up",
  "asking_primary",
  "candidate_question_slot",
  "closing",
  "completed",
  "assessing",
  "assessed",
  "failed",
]);
export const InterviewTurnActorSchema = z.enum(["system", "assistant", "user"]);
export const InterviewTurnKindSchema = z.enum([
  "system_start",
  "opening",
  "primary_question",
  "follow_up",
  "recovery_prompt",
  "candidate_answer",
  "candidate_question_slot",
  "candidate_question",
  "candidate_question_answer",
  "closing",
]);
export const InterviewAnswerAdequacySchema = z.enum(["strong", "partial", "weak", "non_answer"]);
export const InterviewFollowUpReasonSchema = z.enum([
  "needs_example",
  "needs_metric",
  "needs_clarity",
  "interesting_depth",
]);
export const InterviewTurnAnswerScopeSchema = z.enum([
  "role_aligned",
  "adjacent_skill_probe",
  "explicit_gap_disclosed",
]);
export const InterviewFollowUpFocusSchema = z.enum([
  "experience",
  "conceptual_understanding",
  "transferable_skills",
  "learning_plan",
  "none",
]);

export const InterviewInterviewerPersonaSchema = z.object({
  name: z.string(),
  title: z.string(),
  demeanor: z.string(),
});

export const InterviewBlueprintFocusAreaSchema = z.object({
  id: z.string(),
  area: z.string(),
  rationale: z.string(),
  weight: z.number().min(1).max(5),
});

export const InterviewBlueprintQuestionSchema = z.object({
  id: z.string(),
  order: z.number().int().min(1),
  stage: InterviewQuestionStageSchema,
  category: InterviewQuestionCategorySchema,
  focusArea: z.string(),
  prompt: z.string(),
  intent: z.string(),
  idealSignals: z.array(z.string()).max(6),
  defaultFollowUpGoal: z.string(),
});

export type InterviewBlueprintQuestion = z.infer<typeof InterviewBlueprintQuestionSchema>;

export const InterviewBlueprintAIFocusAreaSchema = z.object({
  area: z.string(),
  rationale: z.string(),
  weight: z.number(),
});

export const InterviewBlueprintAIQuestionSchema = z.object({
  stage: InterviewQuestionStageSchema,
  category: InterviewQuestionCategorySchema,
  focusArea: z.string(),
  prompt: z.string(),
  intent: z.string(),
  idealSignals: z.array(z.string()).max(6),
  defaultFollowUpGoal: z.string(),
});

export const InterviewBlueprintAISchema = z.object({
  difficulty: InterviewDifficultySchema,
  targetDurationMinutes: z.number().int().min(8).max(45),
  interviewerPersona: InterviewInterviewerPersonaSchema,
  focusAreas: z.array(InterviewBlueprintAIFocusAreaSchema).min(1).max(6),
  questions: z.array(InterviewBlueprintAIQuestionSchema).min(4).max(8),
});

export type InterviewBlueprintAI = z.infer<typeof InterviewBlueprintAISchema>;

export const InterviewBlueprintSchema = z.object({
  version: z.literal(2),
  difficulty: InterviewDifficultySchema,
  targetDurationMinutes: z.number().int().min(8).max(45),
  interviewerPersona: InterviewInterviewerPersonaSchema,
  focusAreas: z.array(InterviewBlueprintFocusAreaSchema).min(1).max(6),
  questions: z.array(InterviewBlueprintQuestionSchema).min(4).max(8),
  maxFollowUpsPerQuestion: z.number().int().min(0).max(1),
  maxFollowUpsTotal: z.number().int().min(0).max(3),
  candidateQuestionSlot: z.boolean(),
});

export type InterviewBlueprint = z.infer<typeof InterviewBlueprintSchema>;

export const InterviewTurnEvaluationSchema = z.object({
  answeredQuestion: z.boolean(),
  adequacy: InterviewAnswerAdequacySchema,
  confidence: z.number().min(0).max(100),
  missingSignals: z.array(z.string()).max(5),
  shouldFollowUp: z.boolean(),
  followUpReason: InterviewFollowUpReasonSchema.optional(),
  clarificationRequested: z.boolean().optional(),
  answerScope: InterviewTurnAnswerScopeSchema.default("role_aligned"),
  followUpFocus: InterviewFollowUpFocusSchema.default("experience"),
  disclosedGapSkill: z.string().default(""),
  scoreHint: z.number().min(0).max(100),
  summary: z.string(),
});

export type InterviewTurnEvaluation = z.infer<typeof InterviewTurnEvaluationSchema>;

export const GroqInterviewTurnEvaluationSchema = z.object({
  answeredQuestion: z.boolean(),
  adequacy: InterviewAnswerAdequacySchema,
  confidence: z.number().min(0).max(100),
  missingSignals: z.array(z.string()).max(5),
  shouldFollowUp: z.boolean(),
  followUpReason: InterviewFollowUpReasonSchema.nullable(),
  clarificationRequested: z.boolean(),
  answerScope: InterviewTurnAnswerScopeSchema,
  followUpFocus: InterviewFollowUpFocusSchema,
  disclosedGapSkill: z.string(),
  scoreHint: z.number().min(0).max(100),
  summary: z.string(),
});

export type GroqInterviewTurnEvaluation = z.infer<typeof GroqInterviewTurnEvaluationSchema>;

export const InterviewTurnMetadataSchema = z.object({
  primaryQuestionIndex: z.number().int().min(0).optional(),
  totalPrimaryQuestions: z.number().int().min(0).optional(),
  followUpIndex: z.number().int().min(0).optional(),
  questionCategory: InterviewQuestionCategorySchema.optional(),
  questionStage: InterviewQuestionStageSchema.optional(),
  completionSignalIncluded: z.boolean().optional(),
  evaluation: InterviewTurnEvaluationSchema.optional(),
});

export const InterviewTurnSchema = z.object({
  id: z.string(),
  actor: InterviewTurnActorSchema,
  kind: InterviewTurnKindSchema,
  questionId: z.string().optional(),
  text: z.string(),
  createdAt: z.number().int().nonnegative(),
  metadata: InterviewTurnMetadataSchema.optional(),
});

export type InterviewTurn = z.infer<typeof InterviewTurnSchema>;

export const InterviewRuntimeStateSchema = z.object({
  phase: InterviewRuntimePhaseSchema,
  currentQuestionId: z.string().nullable(),
  currentQuestionIndex: z.number().int().min(0),
  primaryQuestionsAsked: z.number().int().min(0),
  followUpsForCurrentQuestion: z.number().int().min(0),
  followUpsTotal: z.number().int().min(0),
  recoveriesForCurrentQuestion: z.number().int().min(0).default(0),
  candidateQuestionSlotOffered: z.boolean(),
  candidateQuestionAnswered: z.boolean().default(false),
  awaitingCandidateReply: z.boolean(),
  completionReason: z.string().nullable(),
  lastTurnAt: z.number().int().nonnegative().nullable(),
  lastClientTurnId: z.string().nullable(),
});

export type InterviewRuntimeState = z.infer<typeof InterviewRuntimeStateSchema>;

// Interview plan — compact compatibility projection generated from the v2 blueprint
export const InterviewPlanSchema = z.object({
  focusAreas: z
    .array(
      z.object({
        area: z
          .string()
          .describe("Focus area name (e.g., 'Leadership Experience', 'Technical Skills')"),
        reason: z.string().describe("Why this area is important for this role"),
        questionCount: z.number().min(1).max(4).describe("Number of questions for this area"),
      }),
    )
    .describe("Areas to focus on during the interview, derived from JD + resume analysis"),
  totalQuestions: z.number().min(4).max(8).describe("Total planned question count"),
  difficulty: InterviewDifficultySchema.describe("Estimated difficulty based on role level"),
  keySkillsToProbe: z
    .array(z.string())
    .describe("Specific skills from the JD to probe during the interview"),
  resumeGaps: z.array(z.string()).describe("Potential gaps between resume and JD to explore"),
});

export type InterviewPlan = z.infer<typeof InterviewPlanSchema>;

export const AssessmentFeedbackImportanceSchema = z.enum(["major", "minor"]);

export const AssessmentFeedbackItemSchema = z.object({
  text: z.string(),
  grounded_in_transcript: z.boolean(),
  evidence_excerpt: z.string(),
  evidence_summary: z.string(),
  importance: AssessmentFeedbackImportanceSchema,
  requires_claimed_experience: z.boolean(),
});

export const AssessmentQuestionSkillScopeSchema = z.enum([
  "core_required",
  "preferred_optional",
  "stretch_adjacent",
  "unknown",
]);

export type AssessmentFeedbackItem = z.infer<typeof AssessmentFeedbackItemSchema>;

// Per-question analysis in assessment
export const QuestionAnalysisSchema = z.object({
  questionNumber: z.number(),
  question: z.string(),
  userResponse: z.string(),
  score: z.number().min(0).max(100),
  strengths: z.array(z.string()).describe("What the candidate did well"),
  improvements: z.array(z.string()).describe("What could be improved"),
  suggestedAnswer: z.string().describe("Key points for a stronger answer"),
  category: InterviewQuestionCategorySchema,
});

export type QuestionAnalysis = z.infer<typeof QuestionAnalysisSchema>;

export const AssessmentQuestionAnalysisSchema = z.object({
  questionNumber: z.number(),
  question: z.string(),
  userResponse: z.string(),
  score: z.number().min(0).max(100),
  questionSkillScope: AssessmentQuestionSkillScopeSchema,
  explicitSkillGapDisclosed: z.boolean(),
  disclosedGapSkill: z.string(),
  strengths: z.array(AssessmentFeedbackItemSchema),
  improvements: z.array(AssessmentFeedbackItemSchema),
  suggestedAnswer: z.string().describe("Key points for a stronger answer"),
  category: InterviewQuestionCategorySchema,
});

export type AssessmentQuestionAnalysis = z.infer<typeof AssessmentQuestionAnalysisSchema>;

// Full assessment — generated after interview completion
export const InterviewAssessmentSchema = z.object({
  overallScore: z.number().min(0).max(100),
  overallGrade: z.enum(["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D", "F"]),
  questionAnalyses: z.array(QuestionAnalysisSchema),
  communicationScore: z.object({
    clarity: z.number().min(0).max(100),
    conciseness: z.number().min(0).max(100),
    structure: z.number().min(0).max(100),
    confidence: z.number().min(0).max(100),
    overall: z.number().min(0).max(100),
  }),
  contentScore: z.object({
    relevance: z.number().min(0).max(100),
    specificity: z.number().min(0).max(100),
    technicalDepth: z.number().min(0).max(100),
    overall: z.number().min(0).max(100),
  }),
  topStrengths: z.array(z.string()).max(5),
  topImprovements: z.array(z.string()).max(5),
  overallFeedback: z.string(),
  readinessLevel: z.enum(["not_ready", "needs_practice", "almost_ready", "interview_ready"]),
});

export type InterviewAssessment = z.infer<typeof InterviewAssessmentSchema>;

export const InterviewAssessmentGenerationSchema = z.object({
  overallScore: z.number().min(0).max(100),
  overallGrade: z.enum(["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D", "F"]),
  questionAnalyses: z.array(AssessmentQuestionAnalysisSchema),
  communicationScore: z.object({
    clarity: z.number().min(0).max(100),
    conciseness: z.number().min(0).max(100),
    structure: z.number().min(0).max(100),
    confidence: z.number().min(0).max(100),
    overall: z.number().min(0).max(100),
  }),
  contentScore: z.object({
    relevance: z.number().min(0).max(100),
    specificity: z.number().min(0).max(100),
    technicalDepth: z.number().min(0).max(100),
    overall: z.number().min(0).max(100),
  }),
  topStrengths: z.array(AssessmentFeedbackItemSchema).max(5),
  topImprovements: z.array(AssessmentFeedbackItemSchema).max(5),
  overallFeedback: z.string(),
  readinessLevel: z.enum(["not_ready", "needs_practice", "almost_ready", "interview_ready"]),
});

export type InterviewAssessmentGeneration = z.infer<typeof InterviewAssessmentGenerationSchema>;
